import { useMemo, useState } from "react";
import { PanelRightClose, PanelRightOpen, Search, X } from "lucide-react";
import ResizableVarsPanel from "./ResizableVarsPanel";

const FMT = [["upper", "AA", "TUTTO MAIUSCOLO"], ["title", "Aa", "Prima lettera maiuscola"], ["lower", "aa", "tutto minuscolo"], ["titlecase", "Nc", "Nome e Cognome (Title Case)"]];
const norm = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

// Catalogo "Variabili disponibili" con pulsante toggle fisso a lato (non sovrapposto all'editor).
// insertMode: clic sul formato inserisce la variabile nel cursore della lettera aperta; altrimenti la copia.
export default function VarsCatalogPanel({ varCategories, onPick, insertMode, open, onToggle }) {
  const [q, setQ] = useState("");
  const cats = useMemo(() => {
    const n = norm(q.trim());
    if (!n) return varCategories;
    return varCategories
      .map((c) => ({ ...c, vars: norm(c.title).includes(n) ? c.vars || [] : (c.vars || []).filter((v) => norm(`${v.label} ${v.key} ${v.value || ""}`).includes(n)) }))
      .filter((c) => c.vars.length);
  }, [q, varCategories]);
  const verb = insertMode ? "Inserisci" : "Copia";
  return (
    <div className="flex shrink-0 self-start gap-2 sticky top-0" data-testid="gl-vars-catalog-wrap">
      <button type="button" onClick={onToggle} data-testid="gl-vars-toggle" title={open ? "Chiudi variabili disponibili" : "Apri variabili disponibili"}
        className="shrink-0 w-7 py-3 flex flex-col items-center gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 transition-colors">
        {open ? <PanelRightClose className="h-4 w-4" /> : <PanelRightOpen className="h-4 w-4" />}
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest" style={{ writingMode: "vertical-rl" }}>Variabili</span>
      </button>
      {open && (
        <ResizableVarsPanel side="left" storageKey="pgp_gl_catalog_w" defaultWidth={224} min={200} max={500} testId="gl-vars-catalog-resizable" className="max-h-[70vh] overflow-y-auto pgp-card p-3">
        <div data-testid="gl-vars-catalog">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-mono font-bold mb-2">Variabili disponibili</div>
          <p className="text-[10px] text-slate-500 mb-2 leading-snug">{insertMode ? "Clicca un formato per inserire la variabile nel punto del cursore della lettera." : "Clicca un formato per copiare la variabile e incollarla nel testo."}</p>
          <div className="relative mb-3">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca variabile…" data-testid="gl-vars-search"
              className="w-full h-8 pl-7 pr-7 text-xs rounded-md border border-slate-700 bg-slate-900/60 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-500" />
            {q && <button type="button" onClick={() => setQ("")} data-testid="gl-vars-search-clear" title="Cancella ricerca" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-amber-400"><X className="h-3.5 w-3.5" /></button>}
          </div>
          {cats.length === 0 ? (
            <div className="text-[11px] text-slate-500" data-testid="gl-vars-empty">{q ? "Nessuna variabile trovata." : "Nessuna variabile disponibile."}</div>
          ) : cats.map((cat) => (
            <div key={cat.title} className="mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-1">{cat.title}</div>
              <div className="space-y-1.5">
                {(cat.vars || []).map((v) => (
                  <div key={v.key + cat.title} className="flex items-start justify-between gap-1" data-testid={`cat-var-row-${v.key}`}>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-foreground font-medium truncate" title={v.label}>{v.label}</div>
                      {v.value ? (v.key === "utente.firma" ? <div className="mt-0.5" data-testid={`cat-var-preview-${v.key}`} dangerouslySetInnerHTML={{ __html: v.value.replace(/width:\d+px;/, "").replace("max-height:60px", "max-height:28px") }} /> : <div className="text-[12px] text-amber-600 font-mono truncate" title={v.value} data-testid={`cat-var-preview-${v.key}`}>{v.value}</div>) : null}
                    </div>
                    <div className="flex gap-0.5 shrink-0 pt-0.5">
                      {FMT.map(([f, l, t]) => (
                        <button key={f} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(v.key, f)} data-testid={`cat-var-${v.key}-${f}`} title={`${verb} ${t}`}
                          className="text-[9px] font-bold px-1 rounded bg-slate-600 text-white border border-slate-400 hover:bg-amber-500 hover:text-slate-900">{l}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </ResizableVarsPanel>
      )}
    </div>
  );
}
